import { useEffect, useMemo, useState } from 'react'
import { Box, useMediaQuery } from '@mui/material'

import { AddEditOtherServicesModal } from './components/AddEditOtherServicesModal'
import { BulkDistributeBillingModal } from './components/BulkDistributeBillingModal'
import { Autocomplete } from '@components/shared/Autocomplete'
import { budgetPreviews } from './components/BudgetPreviews'
import { BudgetPreviewsWrapper } from '@components/shared/BudgetPreviewsWrapper'
import { Button } from '@components/shared/Button'
import { EditDetailedSummaryModal } from './components/EditDetailedSummaryModal'
import { emotionTheme } from '@styles/theme'
import { getPagePermissions } from '@hooks/useUser/provider'
import { InvoicingClientModal } from '@pages/InvoicingClientModal'
import { mapSubmitOtherServicesFormValues } from './components/AddEditOtherServicesModal/helpers/mapSubmitOtherServicesFormValues'
import { PageIds } from '@hooks/useLayout/types'
import { PageWrapper } from '@components/shared/PageWrapper'
import { parseBudgetPreviewsGrid } from './helpers/parseBudgetPreviewsGrid'
import { canEditInvoicingForGu } from './helpers/canEditInvoicingForGu'
import { parseClientsGrid } from './helpers/parseClientsGrid'
import { parseClientsTotalsGrid } from './helpers/parseClientsTotalsGrid'
import { parseDetailedSummaryGrid } from './helpers/parseDetailedSummaryGrid'
import { parseOtherServicesGrid } from './helpers/parseOtherServicesGrid'
import { parseOtherServicesTotalsGrid } from './helpers/parseOtherServicesTotalsGrid'
import { parseWeeklyBreakdownGrid } from './helpers/parseWeeklyBreakdownGrid'
import { parseWeeklyBreakdownTotalsGrid } from './helpers/parseWeeklyBreakdownTotalsGrid'
import { PreviewsStatus, type PreviewsStatusProps } from '@components/shared/BudgetPreviewsStatus/types'
import { PreviewWrapper } from '@components/shared/PreviewWrapper'
import { ReturnModal } from '@components/modals/ReturnModal'
import { RowTableTotal } from '@components/shared/RowTableTotal'
import { Separator } from '@components/shared/Separator'
import { SeparatorWithLine } from '@components/shared/SeparatorWithLine'
import { Slider } from '@components/shared/Slider'
import { Table } from '@components/shared/Table'
import { Title } from '@components/shared/Title'
import { TrashSimple } from '@assets/svgs/TrashSimple'
import { WeeklyBreakdownFilters } from './components/WeeklyBreakdownFilters'
import { useInvoicing } from './context'
import { useUser } from '@hooks/useUser'
import { WarningBanner } from '@components/shared/WarningBanner'
import { WarningIcon } from '@phosphor-icons/react'

import type { AddEditOtherServicesModalFormProps } from './components/AddEditOtherServicesModal/form/types'
import type { ClientProps, DetailedSummaryResponseProps } from './context/types'
import type { OtherServicesItemProps } from '@pages/InvoicingClientModal/context/types'
import type { WeeklyBreakdownFiltersFormProps } from './components/WeeklyBreakdownFilters/form/types'
import type { PaginationParams } from '@components/shared/Table/components/Pagination/types'
import type { SortProps } from '@components/shared/Table/types'
import type { PermissionsItemProps } from '@pages/AccessProfile/context/types'

import * as S from './styles'

const ADD_OTHER_SERVICES_ALLOWED_PREVIEW_STATUSES: PreviewsStatusProps[] = [
  PreviewsStatus.NOT_STARTED,
  PreviewsStatus.IN_PROGRESS,
  PreviewsStatus.RETURNED_GU
]

const ADD_OTHER_SERVICES_ALLOWED_AFTER_DISTRIBUTION_PREVIEW_STATUSES: PreviewsStatusProps[] = [
  PreviewsStatus.NOT_STARTED,
  PreviewsStatus.IN_PROGRESS,
  PreviewsStatus.RETURNED_GU
]

const EDITABLE_DETAILED_SUMMARY_PREVIEW_STATUSES: PreviewsStatusProps[] = [
  PreviewsStatus.NOT_STARTED,
  PreviewsStatus.IN_PROGRESS,
  PreviewsStatus.RETURNED_GU
]

export const Invoicing = () => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const { filiData, userPermissionsData } = useUser()
  const {
    isClientsLoading,
    isClientsByFilterLoading,
    clientsData,
    getClients,
    getClientsByFilter,
    getOtherServices,
    isOtherServicesLoading,
    otherServicesData,
    createNewOtherService,
    editOtherService,
    removeOtherService,
    isRemoveOtherServiceLoading,
    isWeeklybreakdownLoading,
    weeklybreakdownData,
    getWeeklybreakdown,
    getDetailedSummary,
    detailedSummaryData,
    isDetailedSummaryLoading,
    getPreviewDetails,
    isPreviewDetailsLoading,
    previewDetailsData,
    releasePreviewInvoicing,
    isReleasePreviewInvoicingLoading,
    reversePreviewInvoicing,
    isReversePreviewInvoicingLoading,
    restorePreviewInvoicing,
    isRestorePreviewInvoicingLoading,
    bulkDistributeBilling,
    isBulkDistributeBillingLoading
  } = useInvoicing()

  const [openDetailedSummaryModal, setOpenDetailedSummaryModal] = useState(false)
  const [detailedSummaryModalData, setDetailedSummaryModalData] = useState<DetailedSummaryResponseProps | null>(null)
  const [openOtherServicesModal, setOpenOtherServicesModal] = useState(false)
  const [otherServicesModalData, setOtherServicesModalData] = useState<OtherServicesItemProps | null>(null)
  const [openClientModal, setOpenClientModal] = useState(false)
  const [clientModalData, setClientModalData] = useState<ClientProps | null>(null)
  const [openDeleteOtherServicesModal, setOpenDeleteOtherServicesModal] = useState(false)
  const [openReversePreviewModal, setOpenReversePreviewModal] = useState(false)
  const [openReleasePreviewModal, setOpenReleasePreviewModal] = useState(false)
  const [openRestorePreviewModal, setOpenRestorePreviewModal] = useState(false)
  const [openBulkDistributeModal, setOpenBulkDistributeModal] = useState(false)
  const [selectedClientIndices, setSelectedClientIndices] = useState<number[]>([])
  const [clientSelectionResetKey, setClientSelectionResetKey] = useState(0)
  const [permissions, setPermissions] = useState<PermissionsItemProps | null>(null)
  const [weeklybreakdownSortData, setWeeklybreakdownSortData] = useState<SortProps | null>(null)
  const [weeklybreakdownFormData, setWeeklybreakdownFormData] = useState<WeeklyBreakdownFiltersFormProps | null>(null)
  const weekInProgress = useMemo(() => {
    return previewDetailsData?.previews?.find((item) => item.previewStatus.code === PreviewsStatus.IN_PROGRESS)
      ?.previewNumber
  }, [previewDetailsData?.previews])

  // Número da prévia corrente vem do endpoint detalhe-previa (currentPreviewNumber).
  // Fallback na semana em progresso; sem nenhum dos dois, o rótulo omite o ordinal.
  const currentPreviewNumber = previewDetailsData?.currentPreviewNumber ?? weekInProgress

  const previewLabel = currentPreviewNumber
    ? `${currentPreviewNumber}ª Prévia de Faturamento`
    : 'Prévia de Faturamento'

  const hasEditableDetailedSummaryPreview = useMemo(() => {
    return previewDetailsData?.previews?.some((item) =>
      EDITABLE_DETAILED_SUMMARY_PREVIEW_STATUSES.includes(item.previewStatus.code)
    )
  }, [previewDetailsData?.previews])

  const hasAnyClientIncompleteDistribution = useMemo(() => {
    return clientsData?.items.some((client) => !client.hasCompleteDistribution) ?? false
  }, [clientsData?.items])

  const currentPreviewStatus = useMemo(() => {
    const currentPreview = previewDetailsData?.previews?.find((preview) => preview.isCurrentPreview)

    return currentPreview?.previewStatus.code
  }, [previewDetailsData?.previews])

  const isFirstWeekNotStarted = useMemo(() => {
    return (
      previewDetailsData?.previews?.some(
        (preview) => preview.previewNumber === 1 && preview.previewStatus.code === PreviewsStatus.NOT_STARTED
      ) ?? false
    )
  }, [previewDetailsData?.previews])

  const canEditPage = canEditInvoicingForGu({
    canEditPagePermission: permissions?.canEdit === true,
    currentPreviewStatus,
    isFirstWeekNotStarted
  })

  const canAddOtherServicesButton =
    (currentPreviewStatus === PreviewsStatus.NOT_STARTED || currentPreviewStatus === PreviewsStatus.IN_PROGRESS || currentPreviewStatus === PreviewsStatus.RETURNED_GU)

  const canAddOtherServicesByPreviewStatus = useMemo(() => {
    const allowedStatuses = hasAnyClientIncompleteDistribution
      ? ADD_OTHER_SERVICES_ALLOWED_PREVIEW_STATUSES
      : ADD_OTHER_SERVICES_ALLOWED_AFTER_DISTRIBUTION_PREVIEW_STATUSES

    return !!currentPreviewStatus && allowedStatuses.includes(currentPreviewStatus)
  }, [currentPreviewStatus, hasAnyClientIncompleteDistribution])

  const handleGetWeeklybreakdown = (
    params: PaginationParams | null,
    sortData?: SortProps,
    formData?: WeeklyBreakdownFiltersFormProps | null
  ) => {
    getWeeklybreakdown({
      ...(sortData || weeklybreakdownSortData),
      ...(formData !== undefined ? formData : weeklybreakdownFormData),
      filiCode: filiData?.filiCode!,
      competenceDate: filiData?.startPeriod!,
      currentPage: params?.page || 1,
      itemsPerPage: params?.itemsPerPage || weeklybreakdownData?.itemsPerPage || 10
    })
    !!sortData && setWeeklybreakdownSortData(sortData)
    if (formData !== undefined) setWeeklybreakdownFormData(formData)
  }

  const handleOpenOtherServicesDeleteModal = (data?: OtherServicesItemProps) => {
    setOpenDeleteOtherServicesModal(true)
    !!data && setOtherServicesModalData(data)
  }

  const handleAddEditOtherServices = (formData: AddEditOtherServicesModalFormProps) => {
    if (!canEditPage) return

    const mappedFormData = mapSubmitOtherServicesFormValues({
      filiData,
      formData
    })

    if (!otherServicesModalData) {
      createNewOtherService(mappedFormData).then((isSuccess) => !!isSuccess && setOpenOtherServicesModal(false))
    } else {
      mappedFormData.serviceId = otherServicesModalData?.id

      editOtherService(mappedFormData).then((isSuccess) => {
        if (isSuccess) {
          setOpenOtherServicesModal(false)
          setOtherServicesModalData(null)
        }
      })
    }
  }

  const handleGetOtherServices = (params: PaginationParams | null) => {
    getOtherServices({
      competenceDate: filiData?.startPeriod!,
      filiCode: filiData?.filiCode!,
      currentPage: params?.page || 1,
      itemsPerPage: params?.itemsPerPage || otherServicesData?.itemsPerPage || 10
    })
  }

  const handleOpenDetailedSummaryModal = (data: DetailedSummaryResponseProps) => {
    setDetailedSummaryModalData(data)
    setOpenDetailedSummaryModal(true)
  }

  const handleOpenClientModal = (data: ClientProps) => {
    setOpenClientModal(true)
    setClientModalData(data)
  }

  const handleOpenOtherServicesModal = (data?: OtherServicesItemProps) => {
    setOpenOtherServicesModal(true)
    !!data && setOtherServicesModalData(data)
  }

  useEffect(() => {
    !openOtherServicesModal && setOtherServicesModalData(null)
  }, [openOtherServicesModal])

  const handleGetClientsByFilter = (searchText: string) => {
    getClientsByFilter(
      {
        fatDtCompetencia: filiData?.startPeriod!,
        filiCodigo: filiData?.filiCode!,
        currentPage: 1,
        itemsPerPage: clientsData?.itemsPerPage || 10
      },
      searchText
    )
  }

  const handleGetClients = (params: PaginationParams | null) => {
    getClients({
      fatDtCompetencia: filiData?.startPeriod!,
      filiCodigo: filiData?.filiCode!,
      currentPage: params?.page || 1,
      itemsPerPage: params?.itemsPerPage || clientsData?.itemsPerPage || 10
    })
  }

  const handleGetClientModalData = () => {
    const updatedClientData = clientsData?.items.find((item) => item.cdClienteNl === clientModalData?.cdClienteNl)
    if (updatedClientData) {
      setClientModalData(updatedClientData)
    }
  }

  useEffect(() => {
    if (openClientModal && clientsData && clientModalData) {
      handleGetClientModalData()
    }
  }, [clientsData, openClientModal, clientModalData])

  useEffect(() => {
    if (openClientModal && clientsData && clientModalData && filiData?.startPeriod) {
      handleGetClientModalData()
    }
  }, [filiData?.startPeriod])

  const handleGetInitialData = () => {
    if (filiData?.filiCode && filiData?.filiCode !== 'ALL') {
      handleGetClients(null)
      handleGetOtherServices(null)
      handleGetWeeklybreakdown(null)
      getDetailedSummary({ filiCode: filiData?.filiCode, competenceDate: filiData?.startPeriod })
      getPreviewDetails({ filiCode: filiData?.filiCode, competenceDate: filiData?.startPeriod })
    }
  }

  useEffect(() => {
    handleGetInitialData()
  }, [filiData])

  useEffect(() => {
    if (!openClientModal) {
      setOtherServicesModalData(null)
      setClientModalData(null)
    }
  }, [openClientModal])

  useEffect(() => {
    const pagePermissions = getPagePermissions(PageIds.INVOICING)
    setPermissions(pagePermissions)
  }, [userPermissionsData])

  const weeklyBreakdownGrid = parseWeeklyBreakdownGrid({ data: weeklybreakdownData?.items || [] })

  const otherServicesGrid = parseOtherServicesGrid({
    data: otherServicesData?.items || [],
    onEdit: handleOpenOtherServicesModal,
    onDelete: handleOpenOtherServicesDeleteModal,
    actionsDisabled: !canAddOtherServicesByPreviewStatus || !canEditPage
  })

  const clientsGrid = parseClientsGrid({
    data: clientsData?.items || [],
    onEdit: handleOpenClientModal
  })

  return (
    <>
      <PageWrapper
        breadcrumbData={[
          { label: 'Gestão Financeira' },
          { label: `Faturamento / ${filiData?.filiCode} - ${filiData?.filiName}` }
        ]}
      >
        <WarningBanner message={previewDetailsData?.corporateFoodSegmentMessage} />

        <Title label="Faturamento" />

        <BudgetPreviewsWrapper>
          {isMobile ? (
            <Slider items={budgetPreviews({ data: previewDetailsData })} />
          ) : (
            <PreviewWrapper>{budgetPreviews({ data: previewDetailsData }).map((item) => item)}</PreviewWrapper>
          )}

          <Table
            data={parseBudgetPreviewsGrid({ data: previewDetailsData?.previews || [] })}
            isLoading={isPreviewDetailsLoading}
          />
        </BudgetPreviewsWrapper>

        <Title label="Resumo Detalhado" />

        <Table
          data={parseDetailedSummaryGrid({
            onEdit: handleOpenDetailedSummaryModal,
            data: detailedSummaryData ? [detailedSummaryData] : [],
            actionsDisabled: !clientsData?.allowSendPreview || !hasEditableDetailedSummaryPreview || !canEditPage
          })}
          fixedRow={{
            element: (
              <RowTableTotal
                data={[{ label: 'Faturamento Líquido Total', value: detailedSummaryData?.billingNetTotalAmount || 0 }]}
              />
            )
          }}
          isLoading={isDetailedSummaryLoading}
        />

        <SeparatorWithLine />

        <S.TitleFilterRow>
          <Title label="Detalhamento Semanal" />
          <WeeklyBreakdownFilters
            availableServiceGroupings={weeklybreakdownData?.availableServiceGroupings || []}
            availableSalesItemNames={weeklybreakdownData?.availableSalesItemNames || []}
            onFilter={(formData) => handleGetWeeklybreakdown(null, undefined, formData)}
            isLoading={isWeeklybreakdownLoading}
          />
        </S.TitleFilterRow>

        <Table
          headerTitle={`Detalhamento Semanal`}
          data={weeklyBreakdownGrid}
          totalData={parseWeeklyBreakdownTotalsGrid({
            columns: weeklyBreakdownGrid[0] || [],
            data: weeklybreakdownData
          })}
          footer={{
            pagination: {
              currentPage: weeklybreakdownData?.currentPage,
              itemsPerPage: weeklybreakdownData?.itemsPerPage,
              totalItems: weeklybreakdownData?.totalItems,
              totalPages: weeklybreakdownData?.totalPages,
              onPageChange: (page) => handleGetWeeklybreakdown({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetWeeklybreakdown({ itemsPerPage })
            }
          }}
          onSort={(sortData) => handleGetWeeklybreakdown(null, sortData)}
          isLoading={isWeeklybreakdownLoading}
        />

        <SeparatorWithLine style={{ marginBottom: 24 }} />

        <Table
          headerTitle="Outros Serviços"
          data={otherServicesGrid}
          totalData={parseOtherServicesTotalsGrid({
            columns: otherServicesGrid[0] || [],
            data: otherServicesData
          })}
          footer={{
            actionButton: {
              onClick: () => handleOpenOtherServicesModal(),
              labelButton: 'Adicionar Outros Serviços',
              disabled: !canAddOtherServicesButton
            },
            pagination: {
              currentPage: otherServicesData?.currentPage,
              itemsPerPage: otherServicesData?.itemsPerPage,
              totalItems: otherServicesData?.totalItems,
              totalPages: otherServicesData?.totalPages,
              onPageChange: (page) => handleGetOtherServices({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetOtherServices({ itemsPerPage })
            }
          }}
          isLoading={isOtherServicesLoading}
        />

        <Separator />

        <Title
          label="Clientes"
          action={
            <Box display="flex" alignItems="center" gap={2}>
              {canEditPage && (
                <Button
                  width={190}
                  colorType="solidBlue"
                  disabled={!selectedClientIndices.length}
                  onClick={() => setOpenBulkDistributeModal(true)}
                >
                  Distribuir
                </Button>
              )}
              <Box width={240}>
                <Autocomplete
                  placeholder="Buscar por cliente"
                  onChange={(value) => handleGetClientsByFilter(value)}
                  isLoading={isClientsByFilterLoading}
                />
              </Box>
            </Box>
          }
        />

        <Table
          data={clientsGrid}
          totalData={parseClientsTotalsGrid({
            columns: clientsGrid[0] || [],
            data: clientsData
          })}
          selectable={canEditPage}
          onSelectionChange={setSelectedClientIndices}
          selectionResetKey={`${clientsData?.currentPage}-${clientSelectionResetKey}`}
          disabledRows={(clientsData?.items || [])
            .map((client, index) => (client.hasCompleteDistribution ? index : -1))
            .filter((index) => index !== -1)}
          footer={{
            pagination: {
              currentPage: clientsData?.currentPage,
              itemsPerPage: clientsData?.itemsPerPage,
              totalItems: clientsData?.totalItems,
              totalPages: clientsData?.totalPages,
              onPageChange: (page) => handleGetClients({ page }),
              onItemsPerPageChange: (itemsPerPage) => handleGetClients({ itemsPerPage })
            }
          }}
          isLoading={isClientsLoading || isClientsByFilterLoading}
        />

        <S.Buttons>
          {previewDetailsData?.allowRevertPreview && (
            <Button
              width={isMobile ? '100%' : 190}
              colorType="outlineBlue"
              onClick={() => setOpenReversePreviewModal(true)}
            >
              Reverter Prévia
            </Button>
          )}
        </S.Buttons>

        {canEditPage && (
          <S.Buttons>
            {/* {previewDetailsData && (
              <Button
                width={isMobile ? '100%' : 190}
                colorType="outlineBlue"
                onClick={() => setOpenRestorePreviewModal(true)}
                disabled={weekInProgress !== 1}
              >
                Restaurar Prévia
              </Button>
            )} */}

            {previewDetailsData?.allowReleasePreview && (
              <Button
                width={isMobile ? '100%' : 190}
                colorType="solidBlue"
                onClick={() => setOpenReleasePreviewModal(true)}
              >
                Liberar Prévia
              </Button>
            )}
          </S.Buttons>
        )}
      </PageWrapper>

      <EditDetailedSummaryModal
        data={detailedSummaryModalData}
        open={openDetailedSummaryModal}
        onClose={setOpenDetailedSummaryModal}
        canEdit={canEditPage}
      />

      <AddEditOtherServicesModal
        data={otherServicesModalData as unknown as OtherServicesItemProps}
        open={openOtherServicesModal}
        onClose={setOpenOtherServicesModal}
        clientsList={clientsData?.items ?? []}
        onSubmitForm={handleAddEditOtherServices}
        canEdit={canEditPage}
      />

      <InvoicingClientModal
        data={clientModalData}
        previewStatus={previewDetailsData?.previews || []}
        open={openClientModal}
        onClose={setOpenClientModal}
        canEdit={canEditPage}
      />

      <ReturnModal
        open={openDeleteOtherServicesModal}
        headerIcon={<TrashSimple />}
        headerTitle="Excluir Outros Serviços"
        title={`Você tem certeza que deseja excluir o serviço ${otherServicesModalData?.description}?`}
        message={'Após a exclusão, ele poderá ser adicionado novamente clicando em "Adicionar Outros Serviços"'}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenDeleteOtherServicesModal}
        isLoading={isRemoveOtherServiceLoading}
        onConfirm={() => {
          removeOtherService({
            serviceId: otherServicesModalData?.id!,
            filiCode: filiData?.filiCode!,
            fatDtCompetencia: filiData?.startPeriod!
          }).then((isSuccess) => {
            if (!!isSuccess) {
              setOpenDeleteOtherServicesModal(false)
              setOtherServicesModalData(null)
            }
          })
        }}
      />

      <ReturnModal
        open={openReleasePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Liberar Prévia"
        message={`Tem certeza de que deseja liberar a ${previewLabel}?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReleasePreviewModal}
        isLoading={isReleasePreviewInvoicingLoading}
        onConfirm={() => {
          if (!canEditPage) return

          releasePreviewInvoicing({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReleasePreviewModal(false))
        }}
      />

      <ReturnModal
        open={openReversePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Reverter Prévia"
        message={`Tem certeza de que deseja reverter a ${previewLabel}?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenReversePreviewModal}
        isLoading={isReversePreviewInvoicingLoading}
        onConfirm={() => {
          reversePreviewInvoicing({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenReversePreviewModal(false))
        }}
      />

      <ReturnModal
        open={openRestorePreviewModal}
        headerIcon={<WarningIcon size={24} weight="bold" />}
        headerTitle="Restaurar Prévia"
        message={`Tem certeza de que deseja restaurar a ${previewLabel}?`}
        cancelButtonText="Cancelar"
        confirmButtonText="Confirmar"
        onClose={setOpenRestorePreviewModal}
        isLoading={isRestorePreviewInvoicingLoading}
        onConfirm={() => {
          if (!canEditPage) return

          restorePreviewInvoicing({
            filiCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!
          }).then((isSuccess) => isSuccess && setOpenRestorePreviewModal(false))
        }}
      />

      <BulkDistributeBillingModal
        open={openBulkDistributeModal}
        onClose={() => setOpenBulkDistributeModal(false)}
        loading={isBulkDistributeBillingLoading}
        onConfirm={(formData) => {
          if (!canEditPage) return

          const selectedClients = selectedClientIndices.map((index) => {
            const client = clientsData!.items[index]
            return {
              id: Math.floor(Number(client.cdClienteNl)),
              name: client.desClienteNl,
              startBillingDate: client.fatDataInicio
            }
          })

          bulkDistributeBilling({
            distributionType: formData ? 'BusinessDays' : 'MonthDays',
            actionType: 'Selected',
            branchCode: filiData?.filiCode!,
            competenceDate: filiData?.startPeriod!,
            clients: selectedClients,
            weekdayPercentage: Number(formData?.weekdays?.replace(',', '.') ?? 0),
            saturdayPercentage: Number(formData?.saturday?.replace(',', '.') ?? 0),
            sundayHolidayPercentage: Number(formData?.sunday?.replace(',', '.') ?? 0),
            previousMonthBillingDays: Number(formData?.invoiceDays ?? 0),
            holidays: formData?.holidays?.map((h) => h.date) ?? []
          }).then((isSuccess) => {
            if (isSuccess) {
              setOpenBulkDistributeModal(false)
              setSelectedClientIndices([])
              setClientSelectionResetKey((prev) => prev + 1)
              handleGetInitialData()
            }
          })
        }}
      />
    </>
  )
}
